import dayjs from "dayjs";
import { useFetchSubscription } from "src/queries/hooks/accountSettings";
import { useFetchAdminSubscription } from "src/queries/hooks/admin";
import {
  GO_LIVE_WARNING_DAYS_THRESHOLD,
  RENEWAL_WARNING_DAYS_THRESHOLD,
  TEAM_ROLES
} from "./SubscriptionTab.consts";

const deriveTermState = (currentTerm) => {
  if (!currentTerm) {
    return {
      daysToRenewal: null,
      isActive: false,
      isNotStarted: false,
      formattedDateRange: null,
      showRenewalWarning: false,
      showGoLiveWarning: false
    };
  }

  const { termStartDate, termEndDate, renewalDate } = currentTerm;
  const now = dayjs();
  const start = termStartDate ? dayjs(termStartDate) : null;
  const end = termEndDate ? dayjs(termEndDate) : null;
  const renewal = renewalDate ? dayjs(renewalDate) : null;

  const daysToRenewal = renewal ? renewal.diff(now, "day") : null;
  const daysToTermStart = start ? start.diff(now, "day") : null;
  const isActive = !!start && !start.isAfter(now, "day");
  const isNotStarted = !!start && start.isAfter(now, "day");
  const formattedDateRange =
    start && end ? `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}` : null;
  const showRenewalWarning =
    daysToRenewal !== null && daysToRenewal > 0 && daysToRenewal <= RENEWAL_WARNING_DAYS_THRESHOLD;
  const showGoLiveWarning =
    !currentTerm.goLiveDate &&
    daysToTermStart !== null &&
    daysToTermStart <= GO_LIVE_WARNING_DAYS_THRESHOLD;

  return {
    daysToRenewal,
    isActive,
    isNotStarted,
    formattedDateRange,
    showRenewalWarning,
    showGoLiveWarning
  };
};

const useSubscriptionTab = ({ accountId, isAccountContext, hasSalesforceId }) => {
  const accountQuery = useFetchSubscription({
    accountId,
    enabled: !!accountId && isAccountContext && hasSalesforceId
  });

  const adminQuery = useFetchAdminSubscription({
    accountId,
    enabled: !!accountId && !isAccountContext && hasSalesforceId
  });

  const { data, isLoading, isError } = isAccountContext ? accountQuery : adminQuery;

  const currentTerm = data?.currentTerm ?? null;

  const teamMembers = [];
  if (data?.csm) {
    teamMembers.push({ name: data.csm, role: TEAM_ROLES.csm });
  }
  if (data?.executiveSponsor) {
    teamMembers.push({ name: data.executiveSponsor, role: TEAM_ROLES.executiveSponsor });
  }
  if (data?.solutionArchitect) {
    teamMembers.push({ name: data.solutionArchitect, role: TEAM_ROLES.solutionArchitect });
  }

  return {
    currentTerm,
    teamMembers,
    interactionLimit: data?.interactionLimit ?? null,
    interactionDefinition: data?.interactionDefinition ?? null,
    ...deriveTermState(currentTerm),
    isLoading,
    isError,
    isEmpty: !isLoading && !isError && !currentTerm
  };
};

export default useSubscriptionTab;
